/**
 *  FormC.jsx — Installation Emissions Summary (Sheet C_Emissions&Energy)
 *
 *  Revision (Jun 2026) — aligned to Form A/B aesthetic + sector context (NO gating):
 *    1. Slim single-line heading "Installation Emissions Summary"
 *       (no "FORM C", no "CBAM Communication Template").
 *    2. All top-of-page alerts/chips/legend removed:
 *         · "Form B data loaded — X source stream rows..."
 *         · "X process(es) from Form D loaded..."
 *         · "X process(es) from Form A: P1 - Unnamed..."
 *         · 5-color legend with squares
 *    3. Color palette reduced to Form B's set:
 *         white = user-fill, lblue (#eff6ff) = auto/calculated,
 *         lgrey (#f1f5f9) = blank EU-template cells.
 *       Old palette (#FFFFB3 yellow, #FFE066 emphasis-yellow, #C6EFCE green,
 *       #E7F2E3 lgreen) entirely gone.
 *    4. AuditSidebar wrapper added (same pattern as A/B).
 *    5. Blank EU template cells dropped/greyed:
 *         · Fuel Balance row (i): only Total Fuel + Electricity from sheet
 *         · Fuel Balance row (ii): Total / CBAM / Electricity / non-CBAM
 *         · Fuel Balance row (iii): all 5 results
 *         · GHG Balance row (i): CO₂ / Biomass / N₂O / PFC / Direct
 *         · GHG Balance row (ii): CO₂ / Biomass / N₂O / PFC / Indirect
 *         · GHG Balance row (iii): all 7 results
 *    6. ★ NO SECTOR-BASED COLUMN GATING ★ — all GHG columns (N₂O, PFC,
 *       Indirect) are always editable/displayable, in all sectors. Form C
 *       is a summary view; users may have data in any column regardless of
 *       what their sector is priced for under CBAM.
 *       A sector context strip at the top still shows which gases are
 *       PRICED / TYPICAL for the user's sector — purely FYI, never disables
 *       cells.
 *    7. Compact one-page layout: rows i/ii/iii share a single column header
 *       strip per section, so the visual relationship between data flow
 *       lines is clear and the form fits without scrolling.
 *    8. NEXT on Fuel/GHG tabs, Save & Continue on Data Quality → /form-d/:auditId
 *    9. Soft-card + left-accent-stripe banners (grand total) matching the
 *       Form B pattern.
 *   10. Section labels match Form A's fa-sub-label (small, 2px blue underline).
 *
 *  Behaviour preserved EXACTLY:
 *    - DEFAULT_STATE shape
 *    - derivedFromB useMemo (CFormulas.ghgTotalsFromB, totalFossilEnergy,
 *      electricityTJFromD, indirectFromD)
 *    - results useMemo (direct defaults to Mass Balance + Process Emissions,
 *      plus explicit manual co2/n2o/pfc additions; no biomass;
 *      rest = total - cbam - elec - nonCbam; indirect = manual only;
 *      total = direct + indirect)
 *    - handleDataChange / handleGHGMethodChange / handleDataQualityChange
 *    - calculateFormCProgress / sectionProgress
 *    - Firestore onSnapshot + reset-on-auditId
 *    - saveToFirestore payload (_totals, progressMeta, fuelBalance.fromSheet
 *      merge, ghgBalanceByMethod merge)
 *    - Navigation routes /form-b/:auditId and /form-d/:auditId
 **/

import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { firestore as db } from "./firebase.config";
import { doc, setDoc, onSnapshot } from "firebase/firestore";
import {
  Box, CircularProgress, Tooltip,
} from "@mui/material";

import { useFormContext } from "../context/FormContext";
import AuditSidebar from "../components/AuditSidebar";
import useAuditProgress from "../hooks/useAuditProgress";
import {
  calculateRequiredProgress,
  FORM_C_REQUIRED_FIELDS,
} from "../utils/CbamProgress";

import { C as CFormulas } from "../utils/CbamFormulas";

// ─── Sector-context labels (FYI only — never disables any cell) ──────────────
//   Per Annex II of Regulation (EU) 2023/956:
//     - Direct + Indirect priced: Cement, Fertilisers
//     - Direct only: Iron/Steel, Aluminium, Hydrogen, Electricity
//   PFCs are typical for primary Aluminium (Hall-Héroult anode effects).
//   N₂O is typical for Fertilisers (nitric acid production).
//   These sets drive ONLY the small chip strip at the top of each tab —
//   nothing else.
const SECTORS_PRICED_INDIRECT = new Set(["Cement", "Fertilisers"]);
const SECTORS_TYPICAL_N2O     = new Set(["Fertilisers"]);
const SECTORS_TYPICAL_PFC     = new Set(["Aluminium"]);

// ─── Styles ───────────────────────────────────────────────────────────────────
const STYLES = `
  .fc { font-family: 'IBM Plex Sans', sans-serif; font-size: 13px; color: #161616; }

  /* Slim header — matches Form A/B */
  .fc-header-slim {
    background: #fff;
    border-bottom: 2px solid #2563eb;
    padding: 14px clamp(20px, 3vw, 32px);
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .fc-header-slim .title {
    font-size: 17px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.2px;
  }

  /* Body wrapper */
  .fc-body {
    width: 100%;
    max-width: 1600px;
    margin: 0 auto;
    padding: 14px clamp(20px, 3vw, 32px) 24px;
  }

  /* Tabs — match Form A/B */
  .fc-tabs {
    display: flex;
    gap: 4px;
    border-bottom: 2px solid #2563eb;
    margin-bottom: 18px;
    overflow-x: auto;
  }
  .fc-tab {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    padding: 9px 16px;
    font-size: 13px;
    color: #64748b;
    cursor: pointer;
    border: 1px solid transparent;
    border-bottom: none;
    background: #fff;
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
    white-space: nowrap;
    user-select: none;
    transition: background 0.15s, color 0.15s;
  }
  .fc-tab:hover:not(.active) {
    background: #f1f5f9;
    color: #1e293b;
  }
  .fc-tab.active {
    color: #0f172a;
    font-weight: 700;
    border-color: #cbd5e1;
    background: #fff;
  }
  .fc-tab-num {
    font-family: 'IBM Plex Mono', monospace;
    font-size: 11px;
    font-weight: 700;
    background: #e2e8f0;
    color: #475569;
    padding: 2px 7px;
    border-radius: 4px;
    letter-spacing: 0.5px;
  }
  .fc-tab.active .fc-tab-num { background: #2563eb; color: #fff; }
  .fc-tab-badge {
    padding: 2px 8px;
    border-radius: 99px;
    font-size: 11px;
    font-weight: 700;
    background: #E0E0E0;
    color: #555;
  }
  .fc-tab-badge.done  { background: #DCFCE7; color: #15803D; }
  .fc-tab-badge.warn  { background: #FEF3C7; color: #D97706; }
  .fc-tab-badge.empty { background: #FEE2E2; color: #DC2626; }

  /* Tab panel */
  .fc-tab-panel {
    background: #fff;
    border: 1px solid #cbd5e1;
    border-top: none;
    border-radius: 0 0 8px 8px;
    padding: 16px;
    margin-top: -18px;
    margin-bottom: 16px;
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
  }

  /* Section label — Form A's fa-sub-label */
  .fc-section-label {
    font-size: 12px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 10px;
    padding-bottom: 5px;
    border-bottom: 2px solid #2563eb;
    display: inline-block;
  }

  /* Balance matrix — shared columns across row i/ii/iii */
  .fc-matrix {
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    overflow: hidden;
    background: #fff;
  }
  .fc-matrix-head, .fc-matrix-row {
    display: grid;
    align-items: stretch;
    gap: 0;
  }
  .fc-matrix-head {
    background: #f8fafc;
    border-bottom: 1px solid #cbd5e1;
  }
  .fc-matrix-row {
    border-bottom: 1px solid #f1f5f9;
  }
  .fc-matrix-row:last-child { border-bottom: none; }
  .fc-row-label {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 8px 12px;
    background: #f8fafc;
    border-right: 1px solid #f1f5f9;
    font-size: 11px;
    font-weight: 700;
    color: #2563eb;
    letter-spacing: 0.3px;
    min-height: 56px;
  }
  .fc-row-label .meta {
    font-size: 10px;
    color: #64748b;
    font-weight: 500;
    letter-spacing: 0;
    margin-top: 2px;
  }
  .fc-col-header {
    padding: 8px 10px;
    font-size: 10px;
    font-weight: 700;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    text-align: left;
    border-right: 1px solid #f1f5f9;
  }
  .fc-col-header:last-child { border-right: none; }
  .fc-col-header .unit {
    display: block;
    font-size: 9px;
    color: #94a3b8;
    font-weight: 500;
    text-transform: none;
    letter-spacing: 0;
    margin-top: 2px;
  }
  .fc-cell {
    padding: 6px 8px;
    border-right: 1px solid #f1f5f9;
    display: flex;
    align-items: center;
  }
  .fc-cell:last-child { border-right: none; }
  .fc-cell input {
    width: 100%;
    border: 1px solid #cbd5e1;
    background: #fff;
    padding: 6px 8px;
    border-radius: 4px;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 13px;
    color: #161616;
    outline: none;
    transition: border-color 0.15s, background 0.15s;
  }
  .fc-cell input:focus { border-color: #2563eb; }
  .fc-cell input.lblue {
    background: #eff6ff;
    border-color: #bfdbfe;
  }
  .fc-cell input.lgrey {
    background: #f1f5f9;
    color: #94a3b8;
    border-color: #e2e8f0;
    cursor: not-allowed;
  }

  /* Inline sector context strip (FYI labels only) */
  .fc-context {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 5px;
    margin-bottom: 12px;
    font-size: 11px;
    color: #475569;
    flex-wrap: wrap;
  }
  .fc-context strong { color: #1e293b; font-weight: 700; }
  .fc-context-chip {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 99px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }
  .fc-context-chip.on  { background: #DCFCE7; color: #15803D; }
  .fc-context-chip.off { background: #f1f5f9; color: #94a3b8; }

  /* By-method block */
  .fc-method-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
    margin-top: 8px;
  }
  .fc-method-cell {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .fc-method-cell .lbl {
    font-size: 10px;
    color: #475569;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  /* Grand total — soft data card + accent stripe (matches Form B) */
  .fc-grand-card {
    position: relative;
    background: linear-gradient(180deg, #f8fbff 0%, #eff6ff 100%);
    border: 1px solid #dbeafe;
    border-radius: 6px;
    padding: 14px 16px 16px 20px;
    margin-top: 12px;
    overflow: hidden;
  }
  .fc-grand-card::before {
    content: "";
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 4px;
    background: linear-gradient(180deg, #1e40af 0%, #2563eb 50%, #06b6d4 100%);
  }
  .fc-grand-card .lbl {
    font-size: 9px;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    font-weight: 700;
    margin-bottom: 10px;
  }
  .fc-grand-grid {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 10px 16px;
  }
  .fc-grand-grid .lbl { margin-bottom: 3px; }
  .fc-grand-grid .v {
    font-family: 'IBM Plex Mono', monospace;
    font-weight: 700;
    font-size: 14px;
    color: #1e293b;
    letter-spacing: -0.2px;
  }
  .fc-grand-total {
    border-top: 1px solid #cbd5e1;
    margin-top: 12px;
    padding-top: 10px;
    display: flex;
    justify-content: space-between;
    align-items: baseline;
  }
  .fc-grand-total .lbl {
    font-size: 11px;
    color: #475569;
    font-weight: 700;
    margin-bottom: 0;
  }
  .fc-grand-total .v {
    font-family: 'IBM Plex Mono', monospace;
    font-weight: 700;
    font-size: 20px;
    color: #1e40af;
    letter-spacing: -0.4px;
  }

  /* Data Quality tab — narrative textareas */
  .fc-dq-grid {
    display: grid;
    gap: 12px;
  }
  .fc-dq-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .fc-dq-field label {
    font-size: 11px;
    font-weight: 700;
    color: #475569;
    letter-spacing: 0.3px;
    text-transform: uppercase;
  }
  .fc-dq-field textarea {
    border: 1px solid #cbd5e1;
    border-radius: 5px;
    padding: 8px 10px;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 13px;
    color: #161616;
    outline: none;
    background: #fff;
    resize: vertical;
    min-height: 70px;
  }
  .fc-dq-field textarea:focus { border-color: #2563eb; }

  /* Footer / buttons — match Form A/B */
  .fc-footer-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px solid #e2e8f0;
  }
  .fc-btn-prev {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 9px 18px;
    background: #fff; color: #475569;
    border: 1px solid #cbd5e1;
    font-family: inherit;
    font-size: 13px; font-weight: 600;
    cursor: pointer; border-radius: 4px;
  }
  .fc-btn-prev:hover:not(:disabled) { background: #f1f5f9; }
  .fc-btn-prev:disabled { opacity: 0.4; cursor: not-allowed; }
  .fc-btn-save {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 10px 24px;
    background: #2563eb; color: #fff;
    border: none;
    font-family: inherit;
    font-size: 14px; font-weight: 700;
    cursor: pointer; border-radius: 4px;
    transition: background 0.15s;
  }
  .fc-btn-save:hover { background: #1d4ed8; }
  .fc-btn-save:disabled { background: #cbd5e1; cursor: not-allowed; }

  .fc-toast {
    position: fixed; top: 88px; right: 20px; z-index: 1400;
    background: #22c55e; color: #fff;
    padding: 12px 20px; font-size: 13px; font-weight: 600;
    border-radius: 6px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  }
  .fc-toast.error { background: #dc2626; }
`;

// ─── Number helpers ───────────────────────────────────────────────────────────
const toNum = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  const n = parseFloat(String(v).replace(/,/g, ""));
  return isNaN(n) ? 0 : n;
};

const roundSafe = (x, d = 4) => {
  const n = Number(x);
  if (isNaN(n) || !isFinite(n)) return "";
  return Math.round((n + Number.EPSILON) * Math.pow(10, d)) / Math.pow(10, d);
};

const fmt = (v) => {
  if (v === "" || v === null || v === undefined) return "";
  const n = roundSafe(v);
  return n === 0 ? "0" : String(n);
};

// ─── Default state ────────────────────────────────────────────────────────────
const DEFAULT_STATE = {
  fuelBalance: {
    fromSheet: { totalFuel: "", cbam: "", electricity: "", nonCbam: "", rest: "" },
    manual:    { totalFuel: "", cbam: "", electricity: "", nonCbam: "", rest: "" },
  },
  ghgBalance: {
    fromSheet: { co2: "", biomass: "", n2o: "", pfc: "", direct: "", indirect: "", total: "" },
    manual:    { co2: "", biomass: "", n2o: "", pfc: "", direct: "", indirect: "", total: "" },
  },
  ghgBalanceByMethod: {
    calculationBased: "", totalPFC: "", measurementBased: "", other: "",
  },
  dataQuality: {
    generalInfo: "", justification: "", assurance: "",
  },
};

// ═════════════════════════════════════════════════════════════════════════════
//  Component
// ═════════════════════════════════════════════════════════════════════════════
const FormC = () => {
  const navigate    = useNavigate();
  const { auditId } = useParams();
  const { formsData } = useAuditProgress(auditId);
  const { allFormData, updateForm } = useFormContext();

  //  Sector — pulled for the FYI context strip only; no longer gates any cell
  const installationSector = allFormData?.formA?.sector || "";

  //  FYI flags — drive the chip strip labels at top of each tab; NEVER
  //  used to disable cells or block input. All gas columns are always
  //  fully editable regardless of sector.
  const sectorPricesIndirect = SECTORS_PRICED_INDIRECT.has(installationSector);
  const sectorTypicalN2O     = SECTORS_TYPICAL_N2O.has(installationSector);
  const sectorTypicalPFC     = SECTORS_TYPICAL_PFC.has(installationSector);

  //  Form B + Form D data
  const formBSourceRows = allFormData?.formB?.sourceRows || [];
  const formBPFCRows    = allFormData?.formB?.pfcRows    || [];
  const formBMeasRows   = allFormData?.formB?.measRows   || [];
  const formDProcesses  = allFormData?.formD?.processes  || [];

  // ── State ──────────────────────────────────────────────────────────────────
  const [data,       setData]       = useState({ ...DEFAULT_STATE });
  const [activeTab,  setActiveTab]  = useState("fuelBalance");
  const [isSaving,   setIsSaving]   = useState(false);
  const [isLoaded,   setIsLoaded]   = useState(false);
  const [toast,      setToast]      = useState(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  const sectionProgress = {
    fuelBalance: calculateRequiredProgress(data, FORM_C_REQUIRED_FIELDS.fuelBalance),
    ghgBalance:  calculateRequiredProgress(data, FORM_C_REQUIRED_FIELDS.ghgBalance),
  };

  // ── Auto-save to context ───────────────────────────────────────────────────
  useEffect(() => {
    if (!isLoaded) return;
    updateForm("formC", data);
  }, [data, isLoaded, updateForm]);

  // ── Firestore sync ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!auditId) return;

    setData({ ...DEFAULT_STATE });
    setIsLoaded(false);

    const ref = doc(db, "auditReports", auditId, "form_c", "main");
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          const d = snap.data();
          setData(prev => ({
            ...DEFAULT_STATE, ...prev, ...d,
            fuelBalance:        { ...DEFAULT_STATE.fuelBalance,        ...(d.fuelBalance        || {}) },
            ghgBalance:         { ...DEFAULT_STATE.ghgBalance,         ...(d.ghgBalance         || {}) },
            ghgBalanceByMethod: { ...DEFAULT_STATE.ghgBalanceByMethod, ...(d.ghgBalanceByMethod || {}) },
            dataQuality:        { ...DEFAULT_STATE.dataQuality,        ...(d.dataQuality        || {}) },
          }));
        }
        setIsLoaded(true);
      },
      (e) => {
        console.error("FormC onSnapshot error:", e);
        setIsLoaded(true);
      }
    );

    return () => unsub();
  }, [auditId]);

  // ── derivedFromB — central formula engine (UNCHANGED) ─────────────────────
  const derivedFromB = useMemo(() => {
    const ghg = CFormulas.ghgTotalsFromB(formBSourceRows, formBPFCRows, formBMeasRows);
    const totalFossilTJ = CFormulas.totalFossilEnergy(formBSourceRows, formBMeasRows);

    const energyBio_a = formBSourceRows.reduce((s, r) => s + toNum(r.energyBioTJ), 0);
    const energyBio_c = formBMeasRows.reduce((s, r)   => s + toNum(r.energyBioTJ), 0);
    const totalBioTJ  = energyBio_a + energyBio_c;
    const totalFuelEnergy = totalFossilTJ + totalBioTJ;

    const electricityFromD_TJ = CFormulas.electricityTJFromD(formDProcesses);
    const indirectFromD = CFormulas.indirectFromD(formDProcesses);

    return {
      fuelBalance: {
        totalFuel:   fmt(totalFuelEnergy),
        cbam:        "",
        electricity: fmt(totalFossilTJ + electricityFromD_TJ),
        nonCbam:     "",
        rest:        "",
      },
      ghgBalance: {
        co2:      fmt(ghg.co2),
        biomass:  fmt(ghg.biomass),
        n2o:      "",
        pfc:      fmt(ghg.pfc),
        direct:   fmt(ghg.direct),
        indirect: "",
        total:    "",
      },
      ghgByMethod: {
        calculationBased: fmt(ghg.calcBased),
        totalPFC:         fmt(ghg.pfc),
        measurementBased: fmt(ghg.measBased),
      },
      _indirectFromD: indirectFromD,
    };
  }, [formBSourceRows, formBPFCRows, formBMeasRows, formDProcesses]);

  // ── Results row (UNCHANGED logic) ──────────────────────────────────────────
  const results = useMemo(() => {
    const fb = data.fuelBalance;
    const gb = data.ghgBalance;

    const fuelResult = {
      totalFuel:   fmt(toNum(derivedFromB.fuelBalance.totalFuel)   + toNum(fb.manual.totalFuel)),
      cbam:        fmt(toNum(fb.manual.cbam)),
      electricity: fmt(toNum(fb.manual.electricity)),
      nonCbam:     fmt(toNum(fb.manual.nonCbam)),
      rest: (() => {
        const total = toNum(derivedFromB.fuelBalance.totalFuel) + toNum(fb.manual.totalFuel);
        const cbam  = toNum(fb.manual.cbam);
        const elec  = toNum(fb.manual.electricity);
        const nonC  = toNum(fb.manual.nonCbam);
        if (total === 0 && cbam === 0 && elec === 0 && nonC === 0) return "";
        return fmt(total - cbam - elec - nonC);
      })(),
    };

    // Keep the default Direct Emissions basis aligned with Form B:
    // Mass Balance emissions + Process Emissions. Explicit manual additions
    // remain included in the final result.
    const directResult =
      toNum(derivedFromB.ghgBalance.direct)
      + toNum(gb.manual.co2)
      + toNum(gb.manual.n2o)
      + toNum(gb.manual.pfc);

    const indirectResult = toNum(gb.manual.indirect);
    const totalResult = directResult + indirectResult;

    const ghgResult = {
      co2:      fmt(toNum(derivedFromB.ghgBalance.co2)     + toNum(gb.manual.co2)),
      biomass:  fmt(toNum(derivedFromB.ghgBalance.biomass) + toNum(gb.manual.biomass)),
      n2o:      fmt(toNum(gb.fromSheet.n2o)                + toNum(gb.manual.n2o)),
      pfc:      fmt(toNum(derivedFromB.ghgBalance.pfc)     + toNum(gb.manual.pfc)),
      direct:   fmt(directResult),
      indirect: fmt(indirectResult),
      total:    fmt(totalResult),
    };

    return { fuelResult, ghgResult, totalResult };
  }, [derivedFromB, data.fuelBalance, data.ghgBalance]);

  // ── Handlers (UNCHANGED) ───────────────────────────────────────────────────
  const handleDataChange = (section, row, field, value) => {
    setData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [row]: { ...prev[section][row], [field]: value },
      },
    }));
  };
  const handleGHGMethodChange = (field, value) => {
    setData(prev => ({
      ...prev,
      ghgBalanceByMethod: { ...prev.ghgBalanceByMethod, [field]: value },
    }));
  };
  const handleDataQualityChange = (field, value) => {
    setData(prev => ({
      ...prev,
      dataQuality: { ...prev.dataQuality, [field]: value },
    }));
  };

  const calculateFormCProgress = () => {
    const fuelBalance = calculateRequiredProgress(data, FORM_C_REQUIRED_FIELDS.fuelBalance);
    const ghgBalance  = calculateRequiredProgress(data, FORM_C_REQUIRED_FIELDS.ghgBalance);
    const editableTotal  = fuelBalance.total  + ghgBalance.total;
    const editableFilled = fuelBalance.filled + ghgBalance.filled;
    const progress = Math.round((fuelBalance.progress + ghgBalance.progress) / 2);

    return {
      editableTotal, editableFilled, progress,
      status: progress >= 100 ? "Completed" : progress > 0 ? "In Progress" : "Not Started",
      sections: {
        fuelBalance: { title: "Fuel Balance", ...fuelBalance },
        ghgBalance:  { title: "Greenhouse Gas Emissions Balance and Data Quality", ...ghgBalance },
      },
    };
  };

  // ── Save (UNCHANGED payload shape) ────────────────────────────────────────
  const saveToFirestore = async () => {
    if (!auditId) return;

    const progressMeta = calculateFormCProgress();

    const payload = {
      ...data,
      progressMeta,
      updatedAt: new Date(),
      fuelBalance: {
        ...data.fuelBalance,
        fromSheet: {
          ...derivedFromB.fuelBalance,
          ...data.fuelBalance.fromSheet,
        },
      },
      ghgBalance: {
        ...data.ghgBalance,
        fromSheet: {
          ...derivedFromB.ghgBalance,
          ...data.ghgBalance.fromSheet,
        },
      },
      ghgBalanceByMethod: {
        calculationBased: derivedFromB.ghgByMethod.calculationBased,
        totalPFC:         derivedFromB.ghgByMethod.totalPFC,
        measurementBased: derivedFromB.ghgByMethod.measurementBased,
        other:            data.ghgBalanceByMethod.other,
      },
      _totals: {
        grandTotalCO2e:    results.totalResult,
        directEmissions:   results.ghgResult.direct,
        indirectEmissions: results.ghgResult.indirect,
      },
    };
    const ref = doc(db, "auditReports", auditId, "form_c", "main");
    await setDoc(ref, payload);
    updateForm("formC", payload);
  };

  //  NEXT — save and advance tab
  const handleNext = async () => {
    setIsSaving(true);
    try {
      await saveToFirestore();
      if (activeTab === "fuelBalance") setActiveTab("ghgBalance");
      else if (activeTab === "ghgBalance") setActiveTab("dataQuality");
      window.scrollTo({ top: 0, behavior: "smooth" });
      showToast("Saved");
    } catch (e) {
      console.error(e);
      showToast("Save failed", "error");
    } finally { setIsSaving(false); }
  };

  //  Save & Continue — final tab → Form D
  const handleSaveAndContinue = async () => {
    setIsSaving(true);
    try {
      await saveToFirestore();
      showToast("Form C saved");
      setTimeout(() => navigate(`/form-d/${auditId}`), 500);
    } catch (e) {
      console.error(e);
      showToast("Save failed", "error");
    } finally { setIsSaving(false); }
  };

  const handlePrevTab = () => {
    if (activeTab === "dataQuality") setActiveTab("ghgBalance");
    else if (activeTab === "ghgBalance") setActiveTab("fuelBalance");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ── Tab badge class ────────────────────────────────────────────────────────
  const tabBadgeClass = (pct) =>
    pct === 100 ? "done" : pct > 0 ? "warn" : "empty";

  //  Data Quality: simple heuristic — any of 3 narrative fields filled?
  const dqProgress = (() => {
    const dq = data.dataQuality;
    const filled = ["generalInfo", "justification", "assurance"]
      .filter(k => (dq[k] || "").trim().length > 0).length;
    return Math.round((filled / 3) * 100);
  })();

  // ═══════════════════════════════════════════════════════════════════════════
  // SECTOR CONTEXT STRIP — FYI labels only (no longer disables anything)
  // ═══════════════════════════════════════════════════════════════════════════
  const renderSectorContext = () => (
    <div className="fc-context">
      <span>Sector from Form A: <strong>{installationSector || "(not set)"}</strong></span>
      <Tooltip
        title="Indirect emissions are priced under CBAM for Cement + Fertilisers (Annex II). All sectors may still report indirect data here."
        arrow
      >
        <span className={`fc-context-chip ${sectorPricesIndirect ? "on" : "off"}`}>
          Indirect: {sectorPricesIndirect ? "priced" : "report-only"}
        </span>
      </Tooltip>
      <Tooltip
        title="N₂O is most relevant for Fertilisers (nitric acid production). Other sectors may still report N₂O if applicable."
        arrow
      >
        <span className={`fc-context-chip ${sectorTypicalN2O ? "on" : "off"}`}>
          N₂O: {sectorTypicalN2O ? "typical" : "atypical"}
        </span>
      </Tooltip>
      <Tooltip
        title="PFCs are typical for primary Aluminium (Hall-Héroult anode effects). Other sectors may still report PFC if applicable."
        arrow
      >
        <span className={`fc-context-chip ${sectorTypicalPFC ? "on" : "off"}`}>
          PFC: {sectorTypicalPFC ? "typical" : "atypical"}
        </span>
      </Tooltip>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // TAB 1 — FUEL BALANCE (compact matrix)
  // ═══════════════════════════════════════════════════════════════════════════
  const renderFuelBalanceTab = () => {
    // Layout columns: rowLabel | totalFuel | cbam | electricity | nonCbam | rest
    const cols = "180px repeat(5, 1fr)";

    return (
      <>
        {renderSectorContext()}
        <div className="fc-section-label">Fuel Balance (TJ)</div>

        <div className="fc-matrix">
          {/* Column headers */}
          <div className="fc-matrix-head" style={{ gridTemplateColumns: cols }}>
            <div className="fc-row-label" style={{ background: "transparent" }}></div>
            <div className="fc-col-header">Total Fuel Input<span className="unit">TJ</span></div>
            <div className="fc-col-header">Direct fuel — CBAM goods<span className="unit">TJ</span></div>
            <div className="fc-col-header">Fuel for electricity<span className="unit">TJ</span></div>
            <div className="fc-col-header">Direct fuel — non-CBAM<span className="unit">TJ</span></div>
            <div className="fc-col-header">Rest<span className="unit">TJ (auto)</span></div>
          </div>

          {/* Row (i) — only totalFuel & electricity from sheet B; rest blank per EU template */}
          <div className="fc-matrix-row" style={{ gridTemplateColumns: cols }}>
            <div className="fc-row-label">
              i. From Source Streams<span className="meta">Auto-filled · TJ</span>
            </div>
            <div className="fc-cell">
              <input className="lblue" readOnly value={derivedFromB.fuelBalance.totalFuel || ""} />
            </div>
            <div className="fc-cell"><input className="lgrey" readOnly value="—" /></div>
            <div className="fc-cell">
              <input className="lblue" readOnly value={derivedFromB.fuelBalance.electricity || ""} />
            </div>
            <div className="fc-cell"><input className="lgrey" readOnly value="—" /></div>
            <div className="fc-cell"><input className="lgrey" readOnly value="—" /></div>
          </div>

          {/* Row (ii) — manual: totalFuel correction + cbam + electricity correction + nonCbam.
              "rest" is blank in EU template. */}
          <div className="fc-matrix-row" style={{ gridTemplateColumns: cols }}>
            <div className="fc-row-label">
              ii. Manual entry<span className="meta">Editable · TJ</span>
            </div>
            <div className="fc-cell">
              <input type="number" value={data.fuelBalance.manual.totalFuel || ""}
                onChange={e => handleDataChange("fuelBalance", "manual", "totalFuel", e.target.value)} />
            </div>
            <div className="fc-cell">
              <input type="number" value={data.fuelBalance.manual.cbam || ""}
                onChange={e => handleDataChange("fuelBalance", "manual", "cbam", e.target.value)} />
            </div>
            <div className="fc-cell">
              <input type="number" value={data.fuelBalance.manual.electricity || ""}
                onChange={e => handleDataChange("fuelBalance", "manual", "electricity", e.target.value)} />
            </div>
            <div className="fc-cell">
              <input type="number" value={data.fuelBalance.manual.nonCbam || ""}
                onChange={e => handleDataChange("fuelBalance", "manual", "nonCbam", e.target.value)} />
            </div>
            <div className="fc-cell"><input className="lgrey" readOnly value="—" /></div>
          </div>

          {/* Row (iii) — results */}
          <div className="fc-matrix-row" style={{ gridTemplateColumns: cols }}>
            <div className="fc-row-label">
              iii. Result<span className="meta">(i) + (ii) · Rest = Total − CBAM − Elec − non-CBAM</span>
            </div>
            <div className="fc-cell"><input className="lblue" readOnly value={results.fuelResult.totalFuel || ""} /></div>
            <div className="fc-cell"><input className="lblue" readOnly value={results.fuelResult.cbam || ""} /></div>
            <div className="fc-cell"><input className="lblue" readOnly value={results.fuelResult.electricity || ""} /></div>
            <div className="fc-cell"><input className="lblue" readOnly value={results.fuelResult.nonCbam || ""} /></div>
            <div className="fc-cell"><input className="lblue" readOnly value={results.fuelResult.rest || ""} /></div>
          </div>
        </div>

        {/* Grand total summary */}
        {renderGrandTotal()}
      </>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // TAB 2 — GHG BALANCE (compact matrix + by-method block)
  //   ★ All gas columns always editable — no sector-based disabling ★
  // ═══════════════════════════════════════════════════════════════════════════
  const renderGHGBalanceTab = () => {
    // Layout: rowLabel | co2 | biomass | n2o | pfc | direct | indirect | total
    const cols = "160px repeat(7, 1fr)";

    return (
      <>
        {renderSectorContext()}
        <div className="fc-section-label">GHG Emissions Balance (tCO₂e)</div>

        <div className="fc-matrix">
          {/* Column headers — all enabled, all sectors */}
          <div className="fc-matrix-head" style={{ gridTemplateColumns: cols }}>
            <div className="fc-row-label" style={{ background: "transparent" }}></div>
            <div className="fc-col-header">CO₂<span className="unit">tCO₂e</span></div>
            <div className="fc-col-header">Biomass<span className="unit">tCO₂e</span></div>
            <div className="fc-col-header">N₂O<span className="unit">tCO₂e</span></div>
            <div className="fc-col-header">PFC<span className="unit">tCO₂e</span></div>
            <div className="fc-col-header">Direct<span className="unit">tCO₂e</span></div>
            <div className="fc-col-header">Indirect<span className="unit">tCO₂e</span></div>
            <div className="fc-col-header">Total<span className="unit">tCO₂e</span></div>
          </div>

          {/* Row (i) — from Form B + manual N₂O. Indirect/Total blank per EU template. */}
          <div className="fc-matrix-row" style={{ gridTemplateColumns: cols }}>
            <div className="fc-row-label">
              i. From Source Streams<span className="meta">Auto-filled · tCO₂e</span>
            </div>
            <div className="fc-cell">
              <input className="lblue" readOnly value={derivedFromB.ghgBalance.co2 || ""} />
            </div>
            <div className="fc-cell">
              <input className="lblue" readOnly value={derivedFromB.ghgBalance.biomass || ""} />
            </div>
            <div className="fc-cell">
              <input type="number" value={data.ghgBalance.fromSheet.n2o || ""}
                onChange={e => handleDataChange("ghgBalance", "fromSheet", "n2o", e.target.value)}
                placeholder="Manual" />
            </div>
            <div className="fc-cell">
              <input className="lblue" readOnly value={derivedFromB.ghgBalance.pfc || ""} />
            </div>
            <div className="fc-cell">
              <input className="lblue" readOnly value={derivedFromB.ghgBalance.direct || ""} />
            </div>
            {/* Indirect / Total blank in EU template */}
            <div className="fc-cell"><input className="lgrey" readOnly value="—" /></div>
            <div className="fc-cell"><input className="lgrey" readOnly value="—" /></div>
          </div>

          {/* Row (ii) — manual entries. Direct/Total blank per EU template. */}
          <div className="fc-matrix-row" style={{ gridTemplateColumns: cols }}>
            <div className="fc-row-label">
              ii. Manual entry<span className="meta">Editable · tCO₂e</span>
            </div>
            <div className="fc-cell">
              <input type="number" value={data.ghgBalance.manual.co2 || ""}
                onChange={e => handleDataChange("ghgBalance", "manual", "co2", e.target.value)} />
            </div>
            <div className="fc-cell">
              <input type="number" value={data.ghgBalance.manual.biomass || ""}
                onChange={e => handleDataChange("ghgBalance", "manual", "biomass", e.target.value)} />
            </div>
            <div className="fc-cell">
              <input type="number" value={data.ghgBalance.manual.n2o || ""}
                onChange={e => handleDataChange("ghgBalance", "manual", "n2o", e.target.value)} />
            </div>
            <div className="fc-cell">
              <input type="number" value={data.ghgBalance.manual.pfc || ""}
                onChange={e => handleDataChange("ghgBalance", "manual", "pfc", e.target.value)} />
            </div>
            {/* Direct blank per EU template */}
            <div className="fc-cell"><input className="lgrey" readOnly value="—" /></div>
            <div className="fc-cell">
              <input type="number" value={data.ghgBalance.manual.indirect || ""}
                onChange={e => handleDataChange("ghgBalance", "manual", "indirect", e.target.value)}
                placeholder={sectorPricesIndirect ? "Required" : ""} />
            </div>
            {/* Total blank per EU template */}
            <div className="fc-cell"><input className="lgrey" readOnly value="—" /></div>
          </div>

          {/* Row (iii) — results */}
          <div className="fc-matrix-row" style={{ gridTemplateColumns: cols }}>
            <div className="fc-row-label">
              iii. Result<span className="meta">Direct = CO₂ + PFC + manual · Total = Direct + Indirect</span>
            </div>
            <div className="fc-cell"><input className="lblue" readOnly value={results.ghgResult.co2 || ""} /></div>
            <div className="fc-cell"><input className="lblue" readOnly value={results.ghgResult.biomass || ""} /></div>
            <div className="fc-cell"><input className="lblue" readOnly value={results.ghgResult.n2o || ""} /></div>
            <div className="fc-cell"><input className="lblue" readOnly value={results.ghgResult.pfc || ""} /></div>
            <div className="fc-cell"><input className="lblue" readOnly value={results.ghgResult.direct || ""} /></div>
            <div className="fc-cell"><input className="lblue" readOnly value={results.ghgResult.indirect || ""} /></div>
            <div className="fc-cell"><input className="lblue" readOnly value={results.ghgResult.total || ""} /></div>
          </div>
        </div>

        {/* By-method block — compact, always all 4 cells enabled */}
        <div className="fc-section-label" style={{ marginTop: 16 }}>By monitoring methodology (tCO₂e)</div>
        <div className="fc-method-grid">
          <div className="fc-method-cell">
            <span className="lbl">Calculation-based (B-a)</span>
            <input className="lblue" readOnly
              value={derivedFromB.ghgByMethod.calculationBased || ""}
              style={{ width: "100%", padding: "6px 8px", borderRadius: 4,
                       border: "1px solid #bfdbfe", fontFamily: "IBM Plex Sans",
                       fontSize: 13 }} />
          </div>
          <div className="fc-method-cell">
            <span className="lbl">Total PFC (B-b)</span>
            <input className="lblue" readOnly
              value={derivedFromB.ghgByMethod.totalPFC || ""}
              style={{ width: "100%", padding: "6px 8px", borderRadius: 4,
                       border: "1px solid #bfdbfe", fontFamily: "IBM Plex Sans",
                       fontSize: 13 }} />
          </div>
          <div className="fc-method-cell">
            <span className="lbl">Measurement-based (B-c)</span>
            <input className="lblue" readOnly
              value={derivedFromB.ghgByMethod.measurementBased || ""}
              style={{ width: "100%", padding: "6px 8px", borderRadius: 4,
                       border: "1px solid #bfdbfe", fontFamily: "IBM Plex Sans",
                       fontSize: 13 }} />
          </div>
          <div className="fc-method-cell">
            <span className="lbl">Other (manual)</span>
            <input type="number"
              value={data.ghgBalanceByMethod.other || ""}
              onChange={e => handleGHGMethodChange("other", e.target.value)}
              style={{ width: "100%", padding: "6px 8px", borderRadius: 4,
                       border: "1px solid #cbd5e1", fontFamily: "IBM Plex Sans",
                       fontSize: 13 }} />
          </div>
        </div>

        {renderGrandTotal()}
      </>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // TAB 3 — DATA QUALITY
  // ═══════════════════════════════════════════════════════════════════════════
  const renderDataQualityTab = () => (
    <>
      <div className="fc-section-label">Data Quality &amp; Quality Assurance</div>
      <div className="fc-dq-grid">
        <div className="fc-dq-field">
          <label>i. General information on data quality</label>
          <textarea
            value={data.dataQuality.generalInfo}
            onChange={e => handleDataQualityChange("generalInfo", e.target.value)}
            placeholder="Describe the overall data sources, monitoring approach, and any limitations..."
          />
        </div>
        <div className="fc-dq-field">
          <label>ii. Justification for use of default values</label>
          <textarea
            value={data.dataQuality.justification}
            onChange={e => handleDataQualityChange("justification", e.target.value)}
            placeholder="Where default values were used in place of measured data, explain why..."
          />
        </div>
        <div className="fc-dq-field">
          <label>iii. Information on quality assurance</label>
          <textarea
            value={data.dataQuality.assurance}
            onChange={e => handleDataQualityChange("assurance", e.target.value)}
            placeholder="Describe internal review, verification, calibration of instruments, etc..."
          />
        </div>
      </div>

      {renderGrandTotal()}
    </>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // GRAND TOTAL — soft data card + accent stripe (matches Form B)
  //   All values always shown at full opacity — no sector dimming.
  // ═══════════════════════════════════════════════════════════════════════════
  const renderGrandTotal = () => (
    <div className="fc-grand-card">
      <div className="lbl">Installation summary · from formula engine</div>
      <div className="fc-grand-grid">
        <div>
          <div className="lbl">CO₂ (fossil)</div>
          <div className="v">{derivedFromB.ghgBalance.co2 || "—"}</div>
        </div>
        <div>
          <div className="lbl">Biomass</div>
          <div className="v">{derivedFromB.ghgBalance.biomass || "—"}</div>
        </div>
        <div>
          <div className="lbl">PFC</div>
          <div className="v">{derivedFromB.ghgBalance.pfc || "—"}</div>
        </div>
        <div>
          <div className="lbl">Direct total</div>
          <div className="v">{derivedFromB.ghgBalance.direct || "—"}</div>
        </div>
        <div>
          <div className="lbl">Indirect (row ii)</div>
          <div className="v">{results.ghgResult.indirect || "—"}</div>
        </div>
        <div>
          <div className="lbl">Energy (TJ)</div>
          <div className="v">{derivedFromB.fuelBalance.totalFuel || "—"}</div>
        </div>
      </div>
      <div className="fc-grand-total">
        <span className="lbl">Grand Total CO₂e (Direct + Indirect)</span>
        <span className="v">{results.ghgResult.total || "—"} tCO₂e</span>
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  if (!isLoaded) return (
    <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
      <CircularProgress />
    </Box>
  );

  const isLastTab = activeTab === "dataQuality";

  return (
    <>
      <style>{STYLES}</style>

      <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f8fafc" }}>
        <AuditSidebar displayAuditId={auditId} formsData={formsData} />

        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <div className="fc">

            {/* Slim header */}
            <div className="fc-header-slim">
              <div>
                <div className="title">Installation Emissions Summary</div>
              </div>
            </div>

            <div className="fc-body">

              {/* Tabs */}
              <div className="fc-tabs">
                <div className={`fc-tab ${activeTab === "fuelBalance" ? "active" : ""}`}
                  onClick={() => setActiveTab("fuelBalance")}>
                  <span className="fc-tab-num">01</span>
                  <span>Fuel Balance</span>
                  <span className={`fc-tab-badge ${tabBadgeClass(sectionProgress.fuelBalance.progress)}`}>
                    {sectionProgress.fuelBalance.progress}%
                  </span>
                </div>
                <div className={`fc-tab ${activeTab === "ghgBalance" ? "active" : ""}`}
                  onClick={() => setActiveTab("ghgBalance")}>
                  <span className="fc-tab-num">02</span>
                  <span>GHG Balance</span>
                  <span className={`fc-tab-badge ${tabBadgeClass(sectionProgress.ghgBalance.progress)}`}>
                    {sectionProgress.ghgBalance.progress}%
                  </span>
                </div>
                <div className={`fc-tab ${activeTab === "dataQuality" ? "active" : ""}`}
                  onClick={() => setActiveTab("dataQuality")}>
                  <span className="fc-tab-num">03</span>
                  <span>Data Quality</span>
                  <span className={`fc-tab-badge ${tabBadgeClass(dqProgress)}`}>
                    {dqProgress}%
                  </span>
                </div>
              </div>

              <div className="fc-tab-panel">
                {activeTab === "fuelBalance" && renderFuelBalanceTab()}
                {activeTab === "ghgBalance"  && renderGHGBalanceTab()}
                {activeTab === "dataQuality" && renderDataQualityTab()}
              </div>

              {/* Footer — NEXT (1, 2) or Save & Continue (3) */}
              <div className="fc-footer-bar">
                <div>
                  {activeTab !== "fuelBalance" && (
                    <button
                      className="fc-btn-prev"
                      onClick={handlePrevTab}
                      disabled={isSaving}
                    >
                      ← Previous tab
                    </button>
                  )}
                </div>
                {isLastTab ? (
                  <button
                    className="fc-btn-save"
                    onClick={handleSaveAndContinue}
                    disabled={isSaving}
                  >
                    {isSaving ? "Saving..." : "Save & Continue →"}
                  </button>
                ) : (
                  <button
                    className="fc-btn-save"
                    onClick={handleNext}
                    disabled={isSaving}
                  >
                    {isSaving ? "Saving..." : "Next →"}
                  </button>
                )}
              </div>
            </div>

            {toast && (
              <div className={`fc-toast ${toast.type === "error" ? "error" : ""}`}>
                {toast.type === "error" ? "×" : "✓"} {toast.msg}
              </div>
            )}
          </div>
        </Box>
      </Box>
    </>
  );
};

export default FormC;
